import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { getCachedIdToken } from 'src/modules/firebase';
import { getBackendUrl } from 'src/config/backend';
import { queryKeys } from 'src/queries/keys';

export interface AppSession {
  id: number;
  uuid: string;
  user_id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  created_at: string;
  updated_at: string;
}

export interface AppUsageStats {
  total_time_seconds: number;
  last_30_days_seconds: number;
  previous_30_days_seconds: number;
  percentage_change: number;
  last_used_at?: string;
}

export const useStartAppSession = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.post<AppSession>('/api/app_sessions', {
        started_at: new Date().toISOString(),
      });
      return data;
    },
  });
};

// A string body goes out as text/plain, which needs no CORS preflight: the request leaves
// the WebView in one shot while the app is being suspended. The backend parses it as JSON.
export const sendAppSessionStamp = (sessionId: number, endedAt: Date): boolean => {
  const token = getCachedIdToken();
  if (!token) return false;
  if (typeof navigator.sendBeacon !== 'function') return false;
  const url = `${getBackendUrl()}/api/app_sessions/${sessionId}/end`;
  return navigator.sendBeacon(
    url,
    JSON.stringify({ ended_at: endedAt.toISOString(), token }),
  );
};

export const useGetAppUsageStats = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.appUsageStats(userId),
    queryFn: async () => {
      const { data } = await axios.get<AppUsageStats>(
        `/api/app_sessions/stats/${userId}`,
      );
      return data;
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
  });
};
